import { buildSubmissionGroups, getSubmissionGroupStatus } from './submissionStats';
import { flattenPartnerLayoutsFromApi } from './layoutTreeUtils';
import { isLayoutUuid } from './layoutLinkUtils';

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

const galleryIdFromUrl = (url) => {
  if (!url) return null;
  try {
    const seg = String(url).split('/').filter(Boolean).pop();
    return seg || null;
  } catch {
    return null;
  }
};

const catalogIdentityKeys = (row) => {
  const keys = new Set();
  if (row?._id) keys.add(`id:${String(row._id)}`);
  if (row?.uuid && isLayoutUuid(row.uuid)) keys.add(`uuid:${String(row.uuid).trim()}`);
  return keys;
};

/**
 * Build getByPartner-shaped root rows for successful uploads that appear in
 * the analytics submission feed but are missing from the partner layout catalog.
 *
 * Submissions / Recent Activity are subdomain-scoped event logs; Not Linked
 * reads GET /savedLayout/getByPartner. When those diverge, partners still
 * need to see and link the upload here.
 */
export function supplementCatalogFromSubmissionFeed(
  catalogRows,
  submissionFeedRows,
  listingLinks = []
) {
  if (!Array.isArray(submissionFeedRows) || submissionFeedRows.length === 0) {
    return [];
  }

  const linkedUuids = new Set();
  (Array.isArray(listingLinks) ? listingLinks : []).forEach((link) => {
    const uuid = link?.layoutUuid;
    if (uuid) linkedUuids.add(String(uuid));
  });

  const known = new Set();
  flattenPartnerLayoutsFromApi(catalogRows || []).forEach((row) => {
    catalogIdentityKeys(row).forEach((key) => known.add(key));
  });

  const supplement = [];

  buildSubmissionGroups(submissionFeedRows).forEach((group) => {
    if (getSubmissionGroupStatus(group) !== 'success') return;

    const galleryReady = group.rows.find((r) => r.meta?.event === 'gallery_ready');
    if (!galleryReady) return;

    const uuid =
      galleryReady.detail?.uniqueId && isLayoutUuid(galleryReady.detail.uniqueId)
        ? String(galleryReady.detail.uniqueId).trim()
        : null;

    if (uuid && linkedUuids.has(uuid)) return;

    const mongoIdRaw =
      galleryReady.detail?.galleryId ||
      galleryIdFromUrl(galleryReady.detail?.galleryUrl || group.galleryUrl);
    const mongoId =
      mongoIdRaw && OBJECT_ID_RE.test(String(mongoIdRaw)) ? String(mongoIdRaw) : null;

    const galleryUrlRaw =
      (typeof galleryReady.detail?.galleryUrl === 'string' && galleryReady.detail.galleryUrl) ||
      (typeof group.galleryUrl === 'string' && group.galleryUrl) ||
      null;
    const galleryUrl =
      galleryUrlRaw && /^https?:\/\//i.test(galleryUrlRaw.trim())
        ? galleryUrlRaw.trim()
        : null;

    const identityKeys = [
      ...(mongoId ? [`id:${mongoId}`] : []),
      ...(uuid ? [`uuid:${uuid}`] : []),
    ];
    if (identityKeys.some((key) => known.has(key))) return;

    const uploadEvt = group.rows.find((r) => r.meta?.event === 'floorplan_uploaded');
    const createdAt = uploadEvt?.timestamp || galleryReady.timestamp;
    const thumb =
      galleryReady.detail?.thumbnailImage ||
      galleryReady.detail?.thumbnail ||
      galleryReady.detail?.thumbnailFurnishedImage ||
      null;

    const row = {
      _id: mongoId || (uuid ? `supplement:${uuid}` : null),
      uuid,
      galleryUrl,
      name:
        galleryReady.detail?.name ||
        galleryReady.detail?.layoutName ||
        uploadEvt?.detail?.name ||
        'Floor plan',
      createdAt,
      thumbnailImage: thumb,
      image: thumb,
    };

    if (!row._id && !row.uuid) return;

    supplement.push(row);
    identityKeys.forEach((key) => known.add(key));
  });

  return supplement;
}
